import { useCallback, useEffect, useState } from 'react';
import { getCeremonies, createCeremony } from '../../services/api';
import ScoringGrid from './ScoringGrid';

// The Rumaal Rank ceremonies. Pick one to score, or set the next one up.

const FIELD = 'rounded-md border border-gray-200 bg-white px-3 py-2 text-sm outline-none focus:border-purple-400 focus:ring-2 focus:ring-purple-500/15';

const STATUS_STYLES = {
  draft: { label: 'Draft', className: 'bg-gray-100 text-gray-600' },
  scoring: { label: 'Scoring', className: 'bg-amber-100 text-amber-800' },
  published: { label: 'Published', className: 'bg-green-100 text-green-700' },
};

const longDate = (date) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date || '')) return date || '';
  const [y, m, d] = date.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('en-GB', {
    weekday: 'short', day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC',
  });
};

export default function Ceremonies() {
  const [ceremonies, setCeremonies] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');
  const [date, setDate] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await getCeremonies();
      setCeremonies(data.ceremonies);
    } catch (loadError) {
      setError(loadError.response?.data?.error || 'Could not load ceremonies');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const create = async (event) => {
    event.preventDefault();
    setError('');
    try {
      const { data } = await createCeremony({ name: name.trim(), date });
      setCreating(false);
      setName('');
      setDate('');
      await load();
      // Straight into scoring — setting one up is only ever a prelude to it.
      setOpenId(data.ceremony.id);
    } catch (createError) {
      setError(createError.response?.data?.error || 'Could not create that ceremony');
    }
  };

  if (openId) {
    return <ScoringGrid ceremonyId={openId} onBack={() => { setOpenId(null); load(); }} />;
  }

  return (
    <div className="mx-auto max-w-3xl">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-xl font-bold text-gray-900">Rumaal Rank ceremonies</h2>
        <button
          onClick={() => setCreating((c) => !c)}
          className="rounded-md bg-purple-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-purple-700"
        >
          {creating ? 'Cancel' : '+ New ceremony'}
        </button>
      </div>

      {creating && (
        <form onSubmit={create} className="mb-4 rounded-lg border border-gray-200 bg-white p-4">
          <div className="flex flex-wrap gap-2">
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="September 2026"
              className={`${FIELD} flex-1`}
              autoFocus
            />
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className={FIELD} />
            <button
              type="submit"
              disabled={!name.trim() || !date}
              className="rounded-md bg-purple-600 px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
            >
              Create
            </button>
          </div>
        </form>
      )}

      {error && <p className="mb-3 text-sm text-red-600">{error}</p>}
      {loading && <p className="text-sm text-gray-500">Loading…</p>}

      <div className="space-y-2">
        {ceremonies.map((ceremony) => {
          const style = STATUS_STYLES[ceremony.status] || STATUS_STYLES.draft;
          return (
            <button
              key={ceremony.id}
              onClick={() => setOpenId(ceremony.id)}
              className="flex w-full items-center justify-between gap-3 rounded-lg border border-gray-200 bg-white p-4 text-left transition-all hover:border-gray-300 hover:shadow-sm"
            >
              <div className="min-w-0">
                <div className="truncate font-semibold text-gray-900">{ceremony.name}</div>
                <div className="text-sm text-gray-500">
                  {longDate(ceremony.date)} · {ceremony.studentCount} testing
                </div>
              </div>
              <span className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-medium ${style.className}`}>
                {style.label}
              </span>
            </button>
          );
        })}
      </div>

      {!loading && ceremonies.length === 0 && (
        <div className="rounded-lg border border-dashed border-gray-200 py-10 text-center">
          <p className="text-sm text-gray-500">
            No ceremonies yet. Create one for the next Rumaal Rank day.
          </p>
        </div>
      )}
    </div>
  );
}
